/**
 * Provides an accessible replacement for the browser-native clear control of
 * <input type="search"> fields.
 */
class SearchClearButton {
    /**
     * @param {HTMLElement} control Element marked with data-search-clear.
     */
    init(control) {
        if (!control || control.dataset.searchClearInitialized === 'true') {
            return;
        }

        const input = control.querySelector('[data-search-clear-input]');
        const button = control.querySelector('[data-search-clear-button]');

        if (!input || !button) {
            return;
        }

        this._placeValidationFeedbackAfterSubmit(control, input);
        control.dataset.searchClearInitialized = 'true';

        const syncVisibility = () => {
            button.hidden = input.value.length === 0;
        };

        input.addEventListener('input', syncVisibility);
        button.addEventListener('click', (event) => {
            event.preventDefault();

            input.value = '';
            input.dispatchEvent(new Event('input', {bubbles: true}));
            input.dispatchEvent(new Event('search', {bubbles: true}));
            input.focus();
            syncVisibility();
        });

        syncVisibility();
    }

    /**
     * Keep validation feedback from splitting a Bootstrap input-group between
     * the search control and its submit button. This is needed by search forms
     * where FormZero renders the field feedback before the submit element.
     *
     * The feedback keeps its id and aria-describedby relationship; only its
     * position among the input-group children changes.
     *
     * @param {HTMLElement} control Search clear wrapper.
     * @param {HTMLInputElement} input Search input.
     */
    _placeValidationFeedbackAfterSubmit(control, input) {
        const inputGroup = control.closest?.('.input-group');
        const describedBy = input.getAttribute?.('aria-describedby')?.trim();

        if (!inputGroup || !describedBy) {
            return;
        }

        const submit = inputGroup.querySelector?.('button[type="submit"], input[type="submit"]');
        const documentRef = input.ownerDocument ?? control.ownerDocument ?? null;

        if (!submit || !documentRef) {
            return;
        }

        let anchor = submit;
        for (const id of describedBy.split(/\s+/)) {
            const feedback = documentRef.getElementById?.(id);
            if (!feedback || feedback.parentElement !== inputGroup) {
                continue;
            }

            anchor.insertAdjacentElement('afterend', feedback);
            anchor = feedback;
        }
    }

}

export default SearchClearButton;
